import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildEventMemberSectionsForModal,
  buildPartsByIdMap,
  buildMemberModalMembershipState,
  buildSortedCastingInfo,
  filterParticipatingArtists,
  buildGroupEventMap,
  buildGroupEventOrderMap,
  buildMemberPartScopeTabs,
  buildSnapshotArtistMap,
  resolveNextMemberPartScopeEventUuid,
  summarizeArtistEventPartForMemberModal,
} from './memberModalSharedDataUtils';
import { parsePartIds } from './memberListUtils';

export default function useMemberModalEventState({
  open = false,
  eventId,
  participantSnapshot,
  parts,
  castingInfo,
  progress,
  includedArtistIds = null,
  artistsVisibleOnly = false,
  parsePartIdsFn = parsePartIds,
  pickFirstStringFn,
  useSnapshotArtistsForUnified = false,
  isUnifiedEventGroupChat = false,
}) {
  const partsById = useMemo(() => buildPartsByIdMap(parts), [parts]);
  const sortedCasting = useMemo(() => buildSortedCastingInfo(castingInfo), [castingInfo]);
  const participatingArtists = useMemo(() => filterParticipatingArtists(progress), [progress]);

  const groupEventsForMembership = useMemo(
    () => (Array.isArray(participantSnapshot?.group_events) ? participantSnapshot.group_events : []),
    [participantSnapshot]
  );

  const groupEventOrderMap = useMemo(
    () => buildGroupEventOrderMap(groupEventsForMembership),
    [groupEventsForMembership]
  );
  const groupEventMap = useMemo(
    () => buildGroupEventMap(groupEventsForMembership),
    [groupEventsForMembership]
  );

  const [memberPartScopeEventUuid, setMemberPartScopeEventUuid] = useState(() => String(eventId || ''));
  const wasOpenRef = useRef(false);
  const memberPartScopeTabs = useMemo(
    () => buildMemberPartScopeTabs(groupEventsForMembership),
    [groupEventsForMembership]
  );
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = open;
    if (!open) return;
    setMemberPartScopeEventUuid((prev) =>
      resolveNextMemberPartScopeEventUuid({
        previousValue: wasOpen ? prev : null,
        groupEvents: groupEventsForMembership,
        fallbackEventId: eventId,
      })
    );
  }, [eventId, groupEventsForMembership, open]);

  const snapshotArtistMap = useMemo(
    () => buildSnapshotArtistMap(participantSnapshot),
    [participantSnapshot]
  );

  const buildArtistEventPartSummary = useCallback((artist, fallbackPartLabel = '') => (
    summarizeArtistEventPartForMemberModal({
      artist,
      snapshotArtistMap,
      groupEventOrderMap,
      groupEventMap,
      fallbackPartLabel,
      isUnifiedEventGroupChat,
    })
  ), [groupEventMap, groupEventOrderMap, isUnifiedEventGroupChat, snapshotArtistMap]);

  const {
    artistMembershipLabelMap,
    resolveAdminMembershipLabel,
    resolveConciergeMembershipLabel,
    resolveArtistMembershipLabel,
  } = useMemo(
    () => buildMemberModalMembershipState({
      participantSnapshot,
      groupEvents: groupEventsForMembership,
      artistsVisibleOnly,
    }),
    [artistsVisibleOnly, groupEventsForMembership, participantSnapshot]
  );

  const snapshotArtistsForModal = useMemo(
    () => (Array.isArray(participantSnapshot?.artists) ? participantSnapshot.artists : []),
    [participantSnapshot]
  );
  const useSnapshotArtists = useMemo(
    () => Array.isArray(participantSnapshot?.artists),
    [participantSnapshot]
  );
  const artistsForSections = useMemo(
    () => (useSnapshotArtists ? snapshotArtistsForModal : participatingArtists),
    [participatingArtists, snapshotArtistsForModal, useSnapshotArtists]
  );

  const { decidedSections, undecidedList } = useMemo(
    () => buildEventMemberSectionsForModal({
      artists: artistsForSections,
      includedArtistIds,
      memberPartScopeEventUuid,
      partsById,
      sortedCasting,
      snapshotArtistMap,
      groupEventsForMembership,
      artistMembershipLabelMap,
      buildArtistEventPartSummary,
      parsePartIds: parsePartIdsFn,
      pickFirstStringFn,
      useSnapshotArtistsForUnified: useSnapshotArtists && useSnapshotArtistsForUnified,
      isUnifiedEventGroupChat,
    }),
    [
      artistMembershipLabelMap,
      artistsForSections,
      buildArtistEventPartSummary,
      groupEventsForMembership,
      includedArtistIds,
      memberPartScopeEventUuid,
      parsePartIdsFn,
      partsById,
      pickFirstStringFn,
      snapshotArtistMap,
      sortedCasting,
      isUnifiedEventGroupChat,
      useSnapshotArtists,
      useSnapshotArtistsForUnified,
    ]
  );

  return {
    partsById,
    sortedCasting,
    participatingArtists,
    groupEventsForMembership,
    memberPartScopeEventUuid,
    setMemberPartScopeEventUuid,
    memberPartScopeTabs,
    resolveAdminMembershipLabel,
    resolveConciergeMembershipLabel,
    resolveArtistMembershipLabel,
    decidedSections,
    undecidedList,
  };
}
